import { z } from 'zod';
// H-Checklist: the structured apartment inspection. A project's inspection template (area → work
// category → item) is managed in Master Data; each handover (initial or final) inspects its own
// copy of it, item by item. Rules shared by the API and the browser live here.
export const INSPECTION_STATUSES = ['unchecked', 'ok', 'defect', 'not_relevant'];
export const INSPECTION_STATUS_LABELS = {
    unchecked: 'טרם נבדק',
    ok: 'תקין',
    defect: 'לא תקין',
    not_relevant: 'לא רלוונטי',
};
/** A finding's state: open until it is resolved (fixed) or withdrawn (recorded in error). */
export const FINDING_STATUSES = ['open', 'resolved', 'withdrawn'];
export const FINDING_STATUS_LABELS = {
    open: 'פתוח',
    resolved: 'תוקן',
    withdrawn: 'נרשם בטעות',
};
export const CONTRACTOR_SOURCES = ['default', 'manual'];
export const INSPECTION_LIMITS = {
    itemName: 80,
    modelType: 40,
    modelTypesPerItem: 20,
    templateItems: 1000,
    resolutionNote: 300,
};
// ---- Template (Master Data) ------------------------------------------------------------------
export const inspectionTemplateItemSchema = z
    .object({
    id: z.uuid(),
    areaId: z.uuid(),
    workCategoryId: z.uuid(),
    /** The physical component the item checks (DR-189); null: none named. */
    componentId: z.uuid().nullable(),
    name: z.string(),
    active: z.boolean(),
    /** Apartment model types the item applies to; empty: every apartment. */
    modelTypes: z.array(z.string()),
    /** Of a room only some apartments have: added by the inspector, not started with (DR-195). */
    optional: z.boolean(),
    /** Comes from the company's master checklist (false: the project's own item). */
    fromMaster: z.boolean(),
    rowVersion: z.number().int().min(1),
})
    .strict();
/** `GET`/`PUT /projects/:id/inspection-template`: every item in display order. */
export const inspectionTemplateResponseSchema = z
    .object({
    listVersion: z.string().regex(/^[0-9a-f]{64}$/),
    /** Raised by every saved change; a handover records the revision its checklist came from. */
    revision: z.number().int().min(0),
    items: z.array(inspectionTemplateItemSchema),
    /** The model types of the project's apartments, to choose from. */
    modelTypes: z.array(z.string()),
    /** The company master checklist: the version the project follows, and the latest one. */
    master: z
        .object({
        version: z.number().int().min(1).nullable(),
        latest: z.number().int().min(1).nullable(),
    })
        .strict(),
    permissions: z.object({ edit: z.boolean() }).strict(),
})
    .strict();
const modelTypeSchema = z.string().trim().min(1).max(INSPECTION_LIMITS.modelType);
export const inspectionTemplateItemInputSchema = z
    .object({
    /** null: a new item. */
    id: z.uuid().nullable(),
    areaId: z.uuid(),
    workCategoryId: z.uuid(),
    /** The physical component (DR-189); absent: unchanged (none for a new item). */
    componentId: z.uuid().nullable().optional(),
    name: z.string().trim().min(1).max(INSPECTION_LIMITS.itemName),
    active: z.boolean(),
    modelTypes: z.array(modelTypeSchema).max(INSPECTION_LIMITS.modelTypesPerItem),
})
    .strict();
/**
 * `PUT /projects/:id/inspection-template`: the whole template in display order. Every stored item
 * must be listed (items are deactivated, never removed); a template changed meanwhile is refused.
 */
export const inspectionTemplateSaveRequestSchema = z
    .object({
    listVersion: z.string().regex(/^[0-9a-f]{64}$/),
    items: z.array(inspectionTemplateItemInputSchema).max(INSPECTION_LIMITS.templateItems),
})
    .strict()
    .superRefine((request, context) => {
    const ids = new Set();
    const names = new Set();
    request.items.forEach((item, index) => {
        if (item.id !== null) {
            if (ids.has(item.id))
                context.addIssue({ code: 'custom', message: 'duplicate', path: ['items', index, 'id'] });
            ids.add(item.id);
        }
        const name = `${item.areaId}/${item.workCategoryId}/${item.name.toLowerCase()}`;
        if (names.has(name))
            context.addIssue({ code: 'custom', message: 'duplicate', path: ['items', index, 'name'] });
        names.add(name);
    });
});
// ---- A handover's inspection ------------------------------------------------------------------
/** One item of a handover's checklist: the names as they were when the inspection started. */
export const inspectionItemSchema = z
    .object({
    id: z.uuid(),
    areaId: z.uuid(),
    area: z.string(),
    workCategoryId: z.uuid(),
    workCategory: z.string(),
    /** The physical component and its name when the inspection started (DR-189); null: none. */
    componentId: z.uuid().nullable(),
    component: z.string().nullable(),
    name: z.string(),
    status: z.enum(INSPECTION_STATUSES),
})
    .strict();
/** A room of a handover's inspection, for adding or putting back: its name and its items. */
export const handoverRoomSchema = z
    .object({ areaId: z.uuid(), area: z.string(), items: z.number().int().min(0) })
    .strict();
export const handoverInspectionSchema = z
    .object({
    /** The template revision the items came from; null: the inspection has not started. */
    templateRevision: z.number().int().min(0).nullable(),
    /** The items of the rooms this handover inspects (rooms left out are not listed here). */
    items: z.array(inspectionItemSchema),
    /** Rooms left out of this handover (the apartment has none); their items are kept. */
    excludedRooms: z.array(handoverRoomSchema),
    /** Rooms of the project's template that this handover can add (other apartment types). */
    addableRooms: z.array(handoverRoomSchema),
})
    .strict();
/**
 * `POST /projects/:id/handovers/:handoverId/rooms`: leaves a room out of this handover, or puts it
 * in (back, and with any template items it lacks). The project's template does not change.
 */
export const handoverRoomRequestSchema = z
    .object({
    rowVersion: z.number().int().min(1),
    areaId: z.uuid(),
    action: z.enum(['include', 'exclude']),
})
    .strict();
export const inspectionResultInputSchema = z
    .object({ id: z.uuid(), status: z.enum(INSPECTION_STATUSES) })
    .strict();
/** A room or category is complete when every item has a result; having no defect is not enough. */
export const isComplete = (progress) => progress.total > 0 && progress.checked === progress.total;
export function progressOf(items) {
    return {
        total: items.length,
        checked: items.filter((i) => i.status !== 'unchecked').length,
        defects: items.filter((i) => i.status === 'defect').length,
    };
}
/** Items grouped by area, then by work category, each in order of first appearance. */
export function groupInspection(items) {
    const areas = [];
    for (const item of items) {
        let area = areas.find((a) => a.areaId === item.areaId);
        if (area === undefined) {
            area = { areaId: item.areaId, area: item.area, categories: [] };
            areas.push(area);
        }
        let category = area.categories.find((c) => c.workCategoryId === item.workCategoryId);
        if (category === undefined) {
            category = {
                workCategoryId: item.workCategoryId,
                workCategory: item.workCategory,
                items: [],
            };
            area.categories.push(category);
        }
        category.items.push(item);
    }
    return areas;
}
export function suggestContractor(workCategoryId, workCategories, contractors) {
    if (workCategoryId === null)
        return { kind: 'none' };
    const eligible = contractors.filter((c) => c.active && c.categoryIds.includes(workCategoryId));
    const preferred = workCategories.find((c) => c.id === workCategoryId)?.defaultContractorId;
    if (preferred != null && eligible.some((c) => c.id === preferred))
        return { kind: 'default', contractorId: preferred };
    if (eligible.length >= 2)
        return { kind: 'choose', eligible: eligible.map((c) => c.id) };
    return { kind: 'none' };
}
// ---- The company master checklist (DR-195) ------------------------------------------------------
export const MASTER_CHECKLIST_LIMITS = { items: 2000, text: 80, note: 500 };
/** One check of the company's master checklist: a room, a physical component and what to check. */
export const masterChecklistItemSchema = z
    .object({
    /** Stable across versions; a new item gets a new key. */
    key: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
    room: z.string().trim().min(1).max(MASTER_CHECKLIST_LIMITS.text),
    /** Only some apartments have the room: the inspector adds it per apartment. */
    roomOptional: z.boolean(),
    /** Work category (trade) a defect goes to. */
    category: z.string().trim().min(1).max(MASTER_CHECKLIST_LIMITS.text),
    component: z.string().trim().min(1).max(MASTER_CHECKLIST_LIMITS.text),
    name: z.string().trim().min(1).max(MASTER_CHECKLIST_LIMITS.text),
})
    .strict();
/** `GET /master-checklist`: the published version (if any) and the proposal to start from. */
export const masterChecklistResponseSchema = z
    .object({
    current: z
        .object({
        version: z.number().int().min(1),
        note: z.string(),
        createdAt: z.iso.datetime({ offset: true }),
        items: z.array(masterChecklistItemSchema),
    })
        .strict()
        .nullable(),
    /** The built-in proposal for version 1 (SIVAN), to review before publishing. */
    proposal: z.array(masterChecklistItemSchema),
    /** May publish a new version (a business Super Admin). */
    canPublish: z.boolean(),
})
    .strict();
/** `POST /master-checklist`: publishes the next version (refused if `basedOn` is not the latest). */
export const masterChecklistPublishRequestSchema = z
    .object({
    basedOn: z.number().int().min(1).nullable(),
    note: z.string().trim().min(3).max(MASTER_CHECKLIST_LIMITS.note),
    items: z.array(masterChecklistItemSchema).min(1).max(MASTER_CHECKLIST_LIMITS.items),
})
    .strict()
    .superRefine((request, context) => {
    const keys = new Set();
    const names = new Set();
    const optional = new Map();
    request.items.forEach((item, index) => {
        if (keys.has(item.key))
            context.addIssue({ code: 'custom', message: 'duplicate', path: ['items', index, 'key'] });
        keys.add(item.key);
        const name = `${item.room}|${item.category}|${item.name.toLowerCase()}`;
        if (names.has(name))
            context.addIssue({ code: 'custom', message: 'duplicate', path: ['items', index, 'name'] });
        names.add(name);
        const known = optional.get(item.room);
        if (known !== undefined && known !== item.roomOptional)
            context.addIssue({
                code: 'custom',
                message: 'mixed_room',
                path: ['items', index, 'roomOptional'],
            });
        optional.set(item.room, item.roomOptional);
    });
});
//# sourceMappingURL=inspection.js.map